import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  saveTimeEntry, 
  getPaginatedTimeEntriesWithEvents,
  getTimeEntries
} from '../../services/supabase';
import TimeEntryForm from '../../components/TimeEntry/TimeEntryForm';
import TimeEntryList from '../../components/TimeEntry/TimeEntryList';
import TimeSummaryCard from '../../components/TimeEntry/TimeSummaryCard';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { getLastSevenDaysRange, getCurrentMonthRange, formatDateToYYYYMMDD } from '../../utils/utils';
import Pagination from '../../components/Common/Pagination';

const PageContainer = styled.div`
  // padding: 1rem;
  // max-width: 1200px;
  // margin: 0 10%;
  margin: 0 5rem;

  @media (max-width: 1200px) {
    margin: 0 2%;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin: 0 auto;
  }
`;

const PageTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 1rem;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr;
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  h3 {
    margin-top: 0;
    color: #333;
    margin-bottom: 1rem;
  }
`;

const LoadingMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.2fr;
  gap: 2rem;
  margin-bottom: 2rem;
  @media (max-width: 1050px) {
    grid-template-columns: 1fr;
  }
`;

const RecentEntriesCard = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SectionHeader = styled.h3`
  margin-top: 0;
  color: #333;
  margin-bottom: 1rem;
`;

const TimeTrackingPage = () => {
  const { user } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]); // For paginated table data
  const [allTimeEntries, setAllTimeEntries] = useState([]); // For summary calculations
  const [loading, setLoading] = useState(true); // For initial page load
  const [tableLoading, setTableLoading] = useState(false); // For pagination loading
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const pageSize = 10;

  // Fetch all time entries for summary calculations (only called once or when data changes)
  const fetchAllTimeEntries = async () => {
    try {
      const { data, error: entriesError } = await getTimeEntries(user.id);
      
      if (entriesError) {
        throw entriesError;
      }
      
      setAllTimeEntries(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load summary data');
    }
  };

  // Fetch paginated time entries for table display
  const fetchTimeEntries = async (page = currentPage, isInitialLoad = false) => {
    
    try {
      // Only show full page loading on initial load, otherwise show table loading
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      
      
      const { data, error: entriesError, count, totalPages: pages } = await getPaginatedTimeEntriesWithEvents(
        user.id, 
        page, 
        pageSize
      );
      
      if (entriesError) {
        throw entriesError;
      }
      
      setTimeEntries(data || []);
      setTotalEntries(count);
      setTotalPages(pages);
      
    } catch (err) {
      setError(err.message || 'Failed to load table data');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setTableLoading(false);
      }
    }
  };

  useEffect(() => {
    if (user) {
      // Fetch both all entries (for summary) and paginated entries (for table) on initial load
      Promise.all([
        fetchAllTimeEntries(),
        fetchTimeEntries(1, true) // Pass true for initial load
      ]);
      setCurrentPage(1);
    }
  }, [user]);

  // Re-render the table when the page changes
  const handlePageChange = (page) => {
    if (typeof page !== 'number' || page < 1 || page > totalPages) {
      return;
    }
    
    setCurrentPage(page);
    
    fetchTimeEntries(page, false);
  };

  // Handle form submission
  const handleSubmit = async (formData) => {
    try {
      setError(null);
      setSuccessMessage('');
      
      const { selectedEvent, ...timeData } = formData;
      timeData.user_id = user.id;
      timeData.created_at = new Date().toISOString();

      const { data, error } = await saveTimeEntry(timeData);
      
      if (error) throw error;
      
      // After successful submission, refresh both summary and table data
      setCurrentPage(1);
      await Promise.all([
        fetchAllTimeEntries(),
        fetchTimeEntries(1, false)
      ]);
      
      setSuccessMessage('Time entry saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to save time entry. Please try again.');
    }
  };

  // Calculate summary stats using ALL time entries
  const calculateStats = (entries) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const todayStr = formatDateToYYYYMMDD(today);
    const yesterdayStr = formatDateToYYYYMMDD(yesterday);

    let todayHours = 0, yesterdayHours = 0, weekHours = 0, monthHours = 0;
    
    // Get last 7 days range
    const { start: weekStart, end: weekEnd } = getLastSevenDaysRange();
    const weekStartStr = formatDateToYYYYMMDD(weekStart);
    const weekEndStr = formatDateToYYYYMMDD(weekEnd);

    // Get current month range
    const { start: monthStart } = getCurrentMonthRange();
    const monthStartStr = formatDateToYYYYMMDD(monthStart);

    entries.forEach(entry => {
      const entryDate = entry.start_time ? entry.start_time.slice(0, 10) : '';
      const hours = (entry.duration_minutes || 0) / 60;
      if (entryDate === todayStr) todayHours += hours;
      if (entryDate === yesterdayStr) yesterdayHours += hours;
      if (entryDate >= weekStartStr && entryDate <= weekEndStr) weekHours += hours;
      if (entryDate >= monthStartStr) monthHours += hours;
    });

    return {
      today: todayHours.toFixed(2),
      yesterday: yesterdayHours.toFixed(2),
      thisWeek: weekHours.toFixed(2),
      thisMonth: monthHours.toFixed(2)
    };
  };

  const [stats, setStats] = useState({
    today: 0,
    yesterday: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  
  // Calculate stats from ALL time entries, not just paginated ones
  useEffect(() => {
    const newStats = calculateStats(allTimeEntries);
    setStats(newStats);
  }, [allTimeEntries]);

  // Only show full page loading spinner on initial load
  if (loading) {
    return <PageContainer><LoadingSpinner text="Loading Time Tracking..." /></PageContainer>;
  }

  if (error) {
    return <PageContainer>Error: {error}</PageContainer>;
  }

  if (!user) {
    return <PageContainer>Please sign in to access time tracking.</PageContainer>;
  }

  return (
    <PageContainer>
      <TopBar>
        <PageTitle>Time Tracking</PageTitle>
      </TopBar>
      <MainGrid>
        <div>
          <TimeEntryForm 
            userId={user.id}
            onSubmit={handleSubmit} 
            loading={loading}
            error={error}
            successMessage={successMessage}
          />
        </div>
        <div>
          <TimeSummaryCard
            today={stats.today}
            yesterday={stats.yesterday}
            thisWeek={stats.thisWeek}
            thisMonth={stats.thisMonth}
            weekGoal={40}
            userId={user.id}
            isTracking={false}
            onViewReports={() => {/* handle view reports */}}
          />
        </div>
      </MainGrid>
      <RecentEntriesCard>
        <SectionHeader>Time Entries</SectionHeader>
        {timeEntries.length === 0 && !tableLoading ? (
          <LoadingMessage>No time entries yet.</LoadingMessage>
        ) : (
          <>
            <TimeEntryList 
              entries={timeEntries} 
              onRefresh={() => {
                // When refreshing, update both summary and table data
                Promise.all([
                  fetchAllTimeEntries(),
                  fetchTimeEntries(currentPage, false)
                ]);
              }}
              currentUserId={user.id}
              loading={tableLoading} // Pass table loading state to TimeEntryList
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalEntries}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </RecentEntriesCard>
    </PageContainer>
  );
};

export default TimeTrackingPage; 